import type { Character } from '../types/character';
// import type { SyncStatus as PersistenceSyncStatus } from '../types/persistence';
/**
 * Offline Synchronization Service
 * Manages queuing and syncing of operations when offline/online
 */

export interface SyncOperation {
  id: string;
  type: 'save' | 'delete' | 'load';
  characterId: string;
  data?: Character;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

export interface SyncStatus {
  isOnline: boolean;
  pendingOperations: number;
  lastSyncAttempt?: Date;
  lastSuccessfulSync?: Date;
  syncInProgress: boolean;
}

class OfflineSyncService {
  private syncQueue: SyncOperation[] = [];
  private isOnline: boolean = navigator.onLine;
  private syncInProgress: boolean = false;
  private listeners: Array<(status: SyncStatus) => void> = [];
  private retryTimeouts: Map<string, number> = new Map();

  constructor() {
    this.initializeOnlineStatusListeners();
    this.loadQueueFromStorage();
  }

  // For testing purposes
  public _setOnlineStatus(online: boolean): void {
    this.isOnline = online;
    this.notifyStatusChange();
  }

  /**
   * Initialize online/offline event listeners
   */
  private initializeOnlineStatusListeners(): void {
    const handleOnline = () => {
      this.isOnline = true;
      this.notifyStatusChange();

      // Delay sync to allow network to stabilize
      setTimeout(() => {
        this.processSyncQueue();
      }, 1000);
    };

    const handleOffline = () => {
      this.isOnline = false;
      this.notifyStatusChange();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && this.isOnline && this.syncQueue.length > 0) {
        // App became visible and we're online with pending operations
        setTimeout(() => {
          this.processSyncQueue();
        }, 500);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  /**
   * Load pending operations from localStorage
   */
  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem('rt-sync-queue');
      if (stored) {
        this.syncQueue = JSON.parse(stored);
        this.notifyStatusChange();
      }
    } catch (error) {
      console.error('Failed to load sync queue from storage:', error);
      this.syncQueue = [];
    }
  }

  /**
   * Save sync queue to localStorage
   */
  private saveQueueToStorage(): void {
    try {
      localStorage.setItem('rt-sync-queue', JSON.stringify(this.syncQueue));
    } catch (error) {
      console.error('Failed to save sync queue to storage:', error);
    }
  }

  /**
   * Add operation to sync queue
   */
  public queueOperation(operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount'>): void {
    const queuedOperation: SyncOperation = {
      ...operation,
      id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0
    };

    // Remove any existing operations for the same character and type to avoid duplicates
    this.syncQueue = this.syncQueue.filter(
      (op) => !(op.characterId === queuedOperation.characterId && op.type === queuedOperation.type)
    );

    this.syncQueue.push(queuedOperation);
    this.saveQueueToStorage();
    this.notifyStatusChange();

    // If online, try to process immediately
    if (this.isOnline && !this.syncInProgress) {
      this.processSyncQueue();
    }
  }

  /**
   * Process the sync queue
   */
  public async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.syncInProgress || this.syncQueue.length === 0) {
      return;
    }

    this.syncInProgress = true;
    this.notifyStatusChange();

    const operations = [...this.syncQueue];

    for (const operation of operations) {
      try {
        await this.processOperation(operation);

        // Remove successful operation from queue
        this.syncQueue = this.syncQueue.filter((op) => op.id !== operation.id);

        // Clear any retry timeout
        const timeoutId = this.retryTimeouts.get(operation.id);
        if (timeoutId) {
          clearTimeout(timeoutId);
          this.retryTimeouts.delete(operation.id);
        }
      } catch (error) {
        console.error(`Failed to process sync operation ${operation.id}:`, error);

        // Increment retry count
        const operationIndex = this.syncQueue.findIndex((op) => op.id === operation.id);
        if (operationIndex >= 0) {
          this.syncQueue[operationIndex].retryCount++;

          // Remove if max retries exceeded
          if (this.syncQueue[operationIndex].retryCount >= operation.maxRetries) {
            console.error(
              `Max retries exceeded for operation ${operation.id}, removing from queue`
            );
            this.syncQueue.splice(operationIndex, 1);
          } else {
            // Schedule retry with exponential backoff
            this.scheduleRetry(operation);
          }
        }
      }
    }

    this.syncInProgress = false;
    this.saveQueueToStorage();
    this.notifyStatusChange();
  }

  /**
   * Process a single sync operation
   */
  private async processOperation(operation: SyncOperation): Promise<void> {
    // Import GoogleDriveService dynamically to avoid circular dependencies
    const { googleDriveService } = await import('./GoogleDriveService');

    try {
      switch (operation.type) {
        case 'save':
          if (operation.data) {
            await googleDriveService.save(operation.data);
            this.updateLastSuccessfulSync();
          }
          break;

        case 'delete':
          // Note: GoogleDriveService doesn't have delete method yet
          // await googleDriveService.delete(operation.characterId);
          console.warn('Delete operation not implemented in GoogleDriveService');
          this.updateLastSuccessfulSync();
          break;

        case 'load':
          // Load operations are typically not queued, but included for completeness
          await googleDriveService.load(operation.characterId);
          break;

        default:
          throw new Error(`Unknown operation type: ${operation.type}`);
      }
    } catch (error) {
      // Re-throw with additional context
      throw new Error(
        `Failed to ${operation.type} character ${operation.characterId}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Schedule retry for failed operation with exponential backoff
   */
  private scheduleRetry(operation: SyncOperation): void {
    const backoffMs = Math.min(1000 * Math.pow(2, operation.retryCount), 30000); // Max 30 seconds

    const timeoutId = setTimeout(() => {
      if (this.isOnline) {
        this.processSyncQueue();
      }
    }, backoffMs);

    this.retryTimeouts.set(operation.id, timeoutId as unknown as number);
  }

  /**
   * Get current sync status
   */
  public getSyncStatus(): SyncStatus {
    return {
      isOnline: this.isOnline,
      pendingOperations: this.syncQueue.length,
      lastSyncAttempt:
        this.syncQueue.length > 0
          ? new Date(Math.max(...this.syncQueue.map((op) => op.timestamp)))
          : undefined,
      lastSuccessfulSync: this.getLastSuccessfulSync(),
      syncInProgress: this.syncInProgress
    };
  }

  /**
   * Get last successful sync timestamp from localStorage
   */
  private getLastSuccessfulSync(): Date | undefined {
    try {
      const timestamp = localStorage.getItem('rt-last-successful-sync');
      return timestamp ? new Date(parseInt(timestamp)) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Update last successful sync timestamp
   */
  public updateLastSuccessfulSync(): void {
    try {
      localStorage.setItem('rt-last-successful-sync', Date.now().toString());
      this.notifyStatusChange();
    } catch (error) {
      console.error('Failed to update last successful sync timestamp:', error);
    }
  }

  /**
   * Subscribe to sync status changes
   */
  public subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyStatusChange(): void {
    const status = this.getSyncStatus();
    this.listeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in sync status listener:', error);
      }
    });
  }

  /**
   * Clear all pending operations (use with caution)
   */
  public clearQueue(): void {
    this.syncQueue = [];
    this.retryTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.retryTimeouts.clear();
    this.saveQueueToStorage();
    this.notifyStatusChange();
  }

  /**
   * Force sync attempt (even if offline)
   */
  public forceSyncAttempt(): Promise<void> {
    return this.processSyncQueue();
  }

  /**
   * Get queue details for debugging
   */
  public getQueueDetails(): SyncOperation[] {
    return [...this.syncQueue];
  }

  /**
   * Check if we have network connectivity (more reliable than navigator.onLine)
   */
  public async checkConnectivity(): Promise<boolean> {
    try {
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Manually trigger connectivity check and update status
   */
  public async refreshConnectivityStatus(): Promise<void> {
    const actuallyOnline = await this.checkConnectivity();
    if (actuallyOnline !== this.isOnline) {
      this.isOnline = actuallyOnline;
      this.notifyStatusChange();

      if (actuallyOnline && this.syncQueue.length > 0) {
        this.processSyncQueue();
      }
    }
  }
}

// Export singleton instance
export const offlineSyncService = new OfflineSyncService();
